import React, {
    createContext,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';
import type { ReactNode } from 'react';

import * as amplitude from '@amplitude/analytics-browser';
import { Experiment, Source } from '@amplitude/experiment-js-client';
import { useSelector, useDispatch } from 'react-redux';

// Import directly from sibling modules (not from 'wanderu-analytics') to avoid
// a self-import cycle: the package's index.ts re-exports AmplitudeProvider,
// so importing the package here yields a partially-initialized module at
// load time. Hoisting selectors to module scope surfaced this — render-time
// access used to mask it by the time the function body ran.
import {
    experimentEligibilityReceived,
    selectors as experimentsSelectors,
} from './experimentsSlice';
import { fetchClientVariants, fetchExperimentEligibility } from './utils';
import { selectors as variantsSelectors } from './variantsSlice';
import appSettings from 'wanderu-config';
import Logger from 'wanderu-logger';
import { SnowplowService } from 'wanderu-service-analytics';

import {
    ShouldInitAmplitudePlugin,
    setShouldInitAmplitudeForPlugin,
} from './shouldInitAmplitudePlugin';

import type { ExperimentEligibility } from './experimentsSlice';
import type {
    BrowserConfig,
    BrowserOptions,
    EnrichmentPlugin,
    Event,
} from '@amplitude/analytics-types';
import type { ExperimentClient } from '@amplitude/experiment-js-client';

const logger = Logger.getLogger('AmplitudeProvider');

// Bound on how long real human users wait before their `amplitude.track()`
// calls flush. Picked small enough that humans don't perceive the gap and
// large enough that the eligibility fetch almost always wins (~200–500ms in
// practice). On timeout the provider inits with `optOut: false` ("assume
// human"); the enrichment plugin catches any late-resolving bot for the rest
// of the session.
const ELIGIBILITY_FETCH_TIMEOUT_MS = 3000;

interface AmplitudeContextType {
    amplitude: typeof amplitude;
    experiment?: ExperimentClient;
}

interface AmplitudeProviderProps {
    children: ReactNode;
}

const AmplitudeContext = createContext<AmplitudeContextType | undefined>(
    undefined,
);

class SnowplowIdsPlugin implements EnrichmentPlugin {
    name = 'snowplow-ids-plugin';

    type = 'enrichment' as const;

    // eslint-disable-next-line @typescript-eslint/no-unused-vars, class-methods-use-this
    async setup(config: BrowserConfig) {
        return undefined;
    }

    // eslint-disable-next-line class-methods-use-this
    async execute(event: Event) {
        const newEvent = event;

        const {
            domainUserId,
            domainSessionId,
            domainSessionIdx,
        } = SnowplowService.getDomainUserInfo() as {
            domainUserId: string;
            domainSessionId: string;
            domainSessionIdx: number;
        };

        const networkUserId = SnowplowService.getNetworkUserId() as string;

        if (!newEvent.event_properties) {
            newEvent.event_properties = {};
        }

        newEvent.event_properties.domain_session_id = domainSessionId;
        newEvent.event_properties.domain_session_idx = domainSessionIdx;
        newEvent.event_properties.domain_user_id = domainUserId;
        newEvent.event_properties.network_user_id = networkUserId;

        return newEvent;
    }
}

// Module-level ref to ensure single ExperimentClient instance across renders/remounts
let experiment: ExperimentClient | undefined;

const selectExperimentEligibility = experimentsSelectors.selectExperimentEligibility;
const selectVariantEntities = variantsSelectors.selectEntities;
const selectVariantsHasFetched = variantsSelectors.selectHasFetched;

export function AmplitudeProvider({ children }: AmplitudeProviderProps) {
    const variants = useSelector(selectVariantEntities);
    const experimentEligibility: ExperimentEligibility = useSelector(
        selectExperimentEligibility,
    );
    const hasFetched = useSelector(selectVariantsHasFetched);
    const dispatch = useDispatch();

    const eligibilityRequestedRef = useRef(false);
    const identifySentRef = useRef(false);
    const hasInitFiredRef = useRef(false);
    const variantsFetchRequestedRef = useRef(false);
    // Mirror of the server-view (cookie) device id so the exposure tracking
    // provider can read it without re-capturing a stale closure. Temporary
    // diagnostic for the variant-jumping investigation.
    const cookieDeviceIdRef = useRef<string | undefined>(experimentEligibility?.deviceId);
    cookieDeviceIdRef.current = experimentEligibility?.deviceId;

    const apiKey = appSettings?.config?.amplitude?.apiKey;
    const shouldInitAmplitude = experimentEligibility?.shouldInitAmplitude ?? null;

    // Drive the context value from state so consumers re-render when the
    // experiment client becomes available. The module-scoped `experiment` is
    // the process-wide singleton (prevents double-init across HMR / StrictMode
    // remounts); `experimentClient` below is what actually flows through the
    // React context.
    const [experimentClient, setExperimentClient] = useState<ExperimentClient | undefined>(
        () => experiment,
    );

    useEffect(() => {
        if (!apiKey) {
            logger.event('Amplitude', 'apiKey not found');

            return undefined;
        }

        if (hasInitFiredRef.current) {
            return undefined;
        }

        const performInit = (optOut: boolean) => {
            const initOptions: BrowserOptions = {
                appVersion: appSettings?.env?.build?.version,
                autocapture: {
                    attribution: {
                        resetSessionOnNewCampaign: true,
                    },
                    elementInteractions: true,
                    fileDownloads: true,
                    formInteractions: true,
                    pageViews: {
                        trackOn: () => {
                            // disabling auto page views for pages that manually
                            // track them
                            return !window.location.pathname.includes('depart')
                                && !window.location.pathname.includes('return')
                                && !window.location.pathname.includes('checkout')
                                && !window.location.pathname.includes('tripsummary');
                        },
                    },
                    sessions: true,
                },
                optOut,
                serverUrl: appSettings?.config?.amplitude?.serverUrl,
            };

            // Register before init so autocaptured session_start and initial
            // page_view carry the snowplow ids. The SDK queues pre-init
            // `add()` calls and applies them during init (PR #830).
            amplitude.add(new SnowplowIdsPlugin());

            // https://amplitude.com/docs/sdks/analytics/browser/browser-sdk-2#configure-the-sdk
            amplitude.init(apiKey, initOptions)
                .promise
                .then(
                    (result) => {
                        const deviceId = amplitude.getDeviceId() as string;

                        logger.event('Amplitude', 'loaded', { deviceId, result });

                        // Safety net per FR-15: drops events when
                        // `shouldInitAmplitudeForPlugin === false`. Catches the
                        // timeout race where init fired with `optOut: false`
                        // and eligibility later resolves to `false`. Stays
                        // post-init because it depends on the resolved
                        // eligibility module-level state.
                        amplitude.add(new ShouldInitAmplitudePlugin());

                        SnowplowService.addGlobalContexts([{
                            data: {
                                ampClientId: deviceId,
                            },
                            schema: 'iglu:dev.amp.snowplow/amp_id/jsonschema/1-0-0',
                        }]);

                        // Create the experiment client inside the gated init
                        // path (FR-13). It binds to the Amplitude SDK instance
                        // and depends on the device ID being available, so it
                        // cannot be created before `init()` completes.
                        //
                        // The custom exposureTrackingProvider (PR #830) routes
                        // $exposure through amplitude.track() because the
                        // default provider on initializeWithAmplitudeAnalytics
                        // fails to send them — suspected API/deployment key
                        // mismatch, SDK version, or init order. Override
                        // unblocks experiment instrumentation regardless.
                        if (!experiment) {
                            experiment = Experiment.initializeWithAmplitudeAnalytics(apiKey, {
                                automaticExposureTracking: false,
                                exposureTrackingProvider: {
                                    // Stamp every exposure with both views of the
                                    // device id (server cookie vs Amplitude SDK).
                                    // Temporary diagnostic for variant-jumping:
                                    // a non-zero `device_id_mismatch` rate
                                    // explains residual jumping that survives
                                    // freezing the experiment config.
                                    track: (exposure) => {
                                        const cookieDeviceId = cookieDeviceIdRef.current;
                                        const sdkDeviceId = amplitude.getDeviceId();
                                        const canCompare = Boolean(cookieDeviceId) && Boolean(sdkDeviceId);

                                        amplitude.track('$exposure', {
                                            ...exposure,
                                            cookie_device_id: cookieDeviceId,
                                            // Three-state so Snowflake can distinguish
                                            // "really matched" from "couldn't compare" —
                                            // cached / eligibility-error users land in
                                            // the latter and we don't want them counted
                                            // as matches.
                                            device_id_mismatch: canCompare ? cookieDeviceId !== sdkDeviceId : null,
                                            sdk_device_id: sdkDeviceId,
                                        });
                                    },
                                },
                                initialVariants: { ...variants },
                                source: Source.InitialVariants,
                            });
                        }

                        setExperimentClient(experiment);
                    },
                    (error) => {
                        const { deviceId } = experimentEligibility;

                        logger.event('Amplitude', 'failed', { deviceId, error });
                    },
                );
        };

        if (shouldInitAmplitude !== null) {
            hasInitFiredRef.current = true;
            performInit(!shouldInitAmplitude);

            return undefined;
        }

        // Cached page: eligibility hasn't resolved yet. Bound the wait with
        // `ELIGIBILITY_FETCH_TIMEOUT_MS` and bias toward "assume human" if the
        // fetch is still pending when it fires.
        const timer = setTimeout(() => {
            if (hasInitFiredRef.current) {
                return;
            }

            hasInitFiredRef.current = true;
            performInit(false);
        }, ELIGIBILITY_FETCH_TIMEOUT_MS);

        return () => clearTimeout(timer);
        // `variants` and `experimentEligibility` are read inside the post-init
        // `.then()` block but should not retrigger init. The `hasInitFiredRef`
        // guard makes that explicit, and limiting deps to `[apiKey,
        // shouldInitAmplitude]` matches the spec's §6 contract.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiKey, shouldInitAmplitude]);

    useEffect(() => {
        if (
            !apiKey
            || experimentEligibility?.shouldInitAmplitude !== null
            || eligibilityRequestedRef.current
        ) {
            return;
        }

        eligibilityRequestedRef.current = true;

        fetchExperimentEligibility()
            .then((payload) => {
                dispatch(experimentEligibilityReceived(payload));
            })
            .catch((error) => {
                logger.error('Amplitude', 'eligibility_fetch_failed', { error });

                // Assume human on fetch error (FR-12 / AC-7). Dispatching here
                // resolves `shouldInitAmplitude` to `true` so the init effect
                // picks it up immediately, instead of waiting for the timeout.
                //
                // The payload deliberately omits `deviceId` per the spec's
                // FR-12 contract. The reducer's unconditional
                // `state.deviceId = deviceId` (experimentsSlice.ts) will
                // overwrite it to `undefined`, but this only fires on cached
                // routes (where SSR did not hydrate `deviceId` either), so the
                // overwrite is a no-op in practice.
                dispatch(experimentEligibilityReceived({
                    shouldInitAmplitude: true,
                    userProperties: {},
                }));
            });
    }, [apiKey, dispatch, experimentEligibility]);

    useEffect(() => {
        // Mirror the resolved eligibility into the enrichment plugin's
        // module-level state (FR-16). The plugin reads this synchronously in
        // `execute()`, where React context and Redux are not accessible.
        if (shouldInitAmplitude === null) {
            return;
        }

        setShouldInitAmplitudeForPlugin(shouldInitAmplitude);
    }, [shouldInitAmplitude]);

    useEffect(() => {
        if (
            !experimentEligibility
            || experimentEligibility.shouldInitAmplitude === null
            || identifySentRef.current
        ) {
            return;
        }

        identifySentRef.current = true;

        const {
            shouldInitAmplitude: resolvedShouldInit,
            userProperties,
        } = experimentEligibility;

        const { userAgent } = userProperties || {};

        // Note: Sensitive data (cfBotScore, ja4, ipAddress, asn) is now sent
        // server-side via sendServerSideAmplitudeIdentify() for security
        const identifyEvent = new amplitude.Identify();

        identifyEvent.set('should_init_amplitude', resolvedShouldInit);
        if (userAgent) identifyEvent.set('user_agent', userAgent);

        amplitude.identify(identifyEvent);
    }, [experimentEligibility]);

    useEffect(() => {
        // fetch experiment variants when the client side boots only if we haven't already on the server
        if (
            !experimentClient
            || hasFetched
            || variantsFetchRequestedRef.current
            || experimentEligibility?.shouldInitAmplitude !== true
        ) {
            return;
        }

        variantsFetchRequestedRef.current = true;

        fetchClientVariants({
            deviceId: experimentEligibility.deviceId,
            dispatch,
            experiment: experimentClient,
        });
    }, [dispatch, experimentClient, experimentEligibility, hasFetched]);

    return (
        <AmplitudeContext.Provider value={{ amplitude, experiment: experimentClient }}>
            {children}
        </AmplitudeContext.Provider>
    );
}

export { experiment };

// Test-only: resets the module-scoped singleton so a test can reproduce the
// hard-refresh scenario where `experiment` is undefined at mount. Do not call
// from application code.
export function resetExperimentSingleton() {
    experiment = undefined;
}

export function useAmplitude() {
    const context = useContext(AmplitudeContext);

    if (context === undefined) {
        throw new Error('useAmplitude must be used within an AmplitudeProvider');
    }

    return context;
}
